import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Coffee, ChevronDown, Facebook, Instagram } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import ProductDetailModal from '../components/ProductDetailModal';
import MembershipModal from '../components/MembershipModal';
import FAQModal from '../components/FAQModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { productService } from '../services/productService';
import { authService } from '../services/authService';
// import { products } from '../data/mockData';
import SEO from '../components/SEO';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showMembershipDetails, setShowMembershipDetails] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const membershipDetailsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const heroImages = [
    '/images/DIK_4253.jpg', // Espresso shot
    '/images/DIK_4004.jpg', // Cafe interior
    '/images/DIK_4244.jpg', // Espresso machine
    '/images/DIK_4128.jpg'  // Cafe interior wide
  ];

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false,
    });

    const checkAuthStatus = async () => {
      if (authService.isAuthenticated()) {
        try {
          const response = await authService.me();
          setCurrentUser(response.data.user);
          setIsLoggedIn(true);
        } catch (error) {
          // Token expired or invalid
          console.error("Auth check failed", error);
          authService.logout();
          setIsLoggedIn(false);
          setCurrentUser(null);
        }
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch from admin products API (public access, returns all active products)
        const response = await fetch(`${window.location.origin}/api/admin/products?page=1`);
        const data = await response.json();

        if (data && data.data) {
          // Transform data to match UI expectations
          const formattedProducts = data.data.map(p => {
            // Strip HTML from description for the card preview
            const plainDesc = p.description ? p.description.replace(/<[^>]+>/g, '') : '';

            return {
              ...p,
              description: plainDesc, // Use plain text for card
              htmlDescription: p.description, // Keep original for modal if needed
              region: 'Indonesia' // Default region
            };
          });
          setProducts(formattedProducts);
        }
      } catch (error) {
        console.error("Failed to fetch products", error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);


  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleAboutMembershipClick = () => {
    if (showMembershipDetails) {
      setShowMembershipDetails(false);
    } else {
      setShowMembershipDetails(true);
      setTimeout(() => {
        membershipDetailsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Home"
        description="Discover the authentic taste of Indonesia with Iraga Coffee. Premium coffee beans roasted with passion, sourced directly from local farmers."
      />
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${image})`,
              filter: 'brightness(0.7)',
              opacity: currentImageIndex === index ? 1 : 0,
              zIndex: currentImageIndex === index ? 1 : 0
            }}
          />
        ))}
        <div className="relative z-10 text-center text-white px-4" data-aos="fade-up">
          {/* USER REQUEST 1: Font size correction */}
          <h1 className="text-4xl md:text-7xl font-bold mb-6">
            Iraga Coffee & Roastery
          </h1>
          <p className="text-lg md:text-2xl max-w-3xl mx-auto">
            Discover the authentic taste of Indonesia. Premium coffee beans roasted with passion, sourced directly from local farmers.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section id="about" className="py-12 md:py-20 bg-white">
        {/* USER REQUEST 2: Left/Right styling adjustment (using px-6 for better mobile breathing room) */}
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6" data-aos="fade-right">
              <h2 className="text-3xl md:text-5xl font-bold" style={{ color: '#31415A' }}>Our Story</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Founded in 2018, Iraga Coffee & Roastery began its journey with a simple passion: to create coffee that tastes genuinely good and is made with care. As the brand grew and evolved, we took a new step forward in 2022 by continuing our journey under the name Iraga Coffee & Roastery.
                </p>
                <p>
                  Our roastery was built with the intention of developing blends that reflect our identity–balanced, approachable, and consistent. Through wholesale and custom roasting, we aim to share Iraga's signature blends with cafes and spaces beyond our own, reaching more people while maintaining quality in every batch.
                </p>
                <p>
                  At its core, Iraga is a warm and welcoming coffee place–where good coffee, thoughtful roasting, and honest hospitality come together.
                </p>
              </div>
            </div>
            <div className="relative" data-aos="fade-left">
              <img
                src="/images/DIK_4004.jpg"
                alt="Iraga Coffee Shop"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 md:py-20" style={{ backgroundColor: '#31415A' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 md:mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-5xl font-bold mb-2 md:mb-3" style={{ color: '#FFFFFF' }}>Products</h2>
            <p className="text-sm md:text-lg" style={{ color: '#FFFFFF' }}>Made by a local farm</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4" data-aos="fade-up" data-aos-delay="200">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {products.map((product) => (
                <CarouselItem key={product.id} className="pl-2 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3">
                  <div onClick={() => handleProductClick(product)} className="cursor-pointer h-full">
                    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 bg-white border-none h-full flex flex-col">
                      <div className="relative h-[300px] overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                      <CardHeader>
                        <CardTitle className="text-xl" style={{ color: '#31415A' }}>{product.name}</CardTitle>
                        <CardDescription style={{ color: '#5C5935' }}>{product.region}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex-grow flex flex-col justify-between">
                        <p className="text-gray-700 text-sm mb-4 line-clamp-3">{product.description}</p>
                        <div className="flex justify-end mt-auto">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="group"
                            style={{ color: '#31415A' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductClick(product);
                            }}
                          >
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex text-white hover:bg-white/20 left-2" />
            <CarouselNext className="hidden md:flex text-white hover:bg-white/20 right-2" />
          </Carousel>

          <ProductDetailModal
            isOpen={!!selectedProduct}
            onClose={() => setSelectedProduct(null)}
            product={selectedProduct}
          />
        </div>
      </section>

      {/* Iraga Digital Membership Section */}
      <section id="membership" className="relative min-h-[700px] md:h-[900px] w-full overflow-hidden bg-[#F5F5F0] pb-8 md:pb-32">
        {/* Background */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/bgpaper.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          {/* Top Right Label */}

          <div className="grid md:grid-cols-2 gap-12 h-full pt-16 md:pt-32">
            {/* Left Column: Text */}
            <div className="space-y-6 md:space-y-8 pt-8 md:pt-12 relative z-20 text-center md:text-left" data-aos="fade-right">
              <h2 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight" style={{ color: '#31415A' }}>
                Iraga Digital<br />
                Membership
              </h2>
              <p className="text-lg md:text-xl max-w-md leading-relaxed text-[#31415A] mx-auto md:mx-0">
                Collect the stamp, claim your<br />
                voucher and get 1 free regular<br />
                coffee!
              </p>
            </div>

            {/* Right Column: Visual Elements (Cards) */}
            <div className="relative h-[400px] md:h-full w-full flex items-center justify-center md:block">

              {/* Card 1: VOUCHER CARD (Bottom Left) */}
              <div
                data-aos="fade-up"
                data-aos-delay="200"
                className="relative -top-8 md:top-[150px] md:absolute md:left-0 w-[280px] md:w-[340px] h-[360px] md:h-[500px] shadow-2xl z-20 flex flex-col items-center justify-center text-center p-6 md:p-8 bg-[#F0EBE0] transform -rotate-2"
              >
                <div className="space-y-2 mb-6 md:mb-8">
                  <div className="flex flex-col items-center leading-none">
                    <span className="text-[2.5rem] md:text-[3.5rem] font-sans font-bold text-[#0047AB] tracking-tight leading-tight">
                      GET A FREE DRINK
                    </span>
                  </div>

                  <p className="text-xs md:text-sm font-mono tracking-widest pt-2 md:pt-4 text-[#0047AB] uppercase">
                    get 6th Coffee For get 1 free coffee
                  </p>
                </div>

                {/* Stamps Grid with Wavy Borders */}
                <div className="grid grid-cols-3 gap-x-3 md:gap-x-4 gap-y-4 md:gap-y-6 w-full px-2">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="relative w-[50px] md:w-[60px] h-[50px] md:h-[60px] flex items-center justify-center">
                      {/* Wavy SVG Border */}
                      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full text-[#0047AB]">
                        <path d="M50 5 C55 5 57 10 62 10 C67 10 70 6 74 9 C78 12 78 18 82 21 C86 24 92 23 94 28 C96 33 93 38 95 43 C97 48 100 52 98 57 C96 62 90 63 88 68 C86 73 86 79 82 83 C78 87 73 85 68 88 C63 91 60 96 55 96 C50 96 48 91 43 91 C38 91 35 95 31 92 C27 89 27 83 23 80 C19 77 13 78 11 73 C9 68 12 63 10 58 C8 53 5 49 7 44 C9 39 15 38 17 33 C19 28 19 22 23 18 C27 14 32 16 37 13 C42 10 45 5 50 5 Z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        />
                      </svg>
                      <span className="text-lg md:text-xl font-bold text-[#0047AB] z-10 font-sans tracking-tight">
                        {i < 10 ? `0${i}` : i}
                      </span>
                    </div>
                  ))}
                </div>
              </div>


              {/* Card 2: MEMBERSHIP CARD (Top Right) - Hidden on mobile */}
              <div
                data-aos="fade-left"
                data-aos-delay="400"
                className="hidden md:flex absolute right-[-20px] top-[-40px] w-[320px] h-[480px] shadow-xl transform rotate-[25deg] z-10 items-center justify-center bg-[#F0EBE0]"
              >
                <div className="relative w-full h-full p-8 flex flex-col items-center justify-center">

                  {/* Rotating Circle */}
                  <div className="relative w-56 h-56 flex items-center justify-center">
                    <svg viewBox="0 0 100 100" className="w-full h-full absolute inset-0 animate-spin-slow">
                      <defs>
                        <path id="circlePath" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                      </defs>
                      <text fontSize="8" fontWeight="bold" fill="#4B6A9C" letterSpacing="1.8">
                        <textPath href="#circlePath" startOffset="0%">
                          iraga coffee & roastery
                        </textPath>
                      </text>
                    </svg>

                    {/* Coffee Icon */}
                    <Coffee className="w-16 h-16 text-[#4B6A9C] transform -rotate-[25deg]" strokeWidth={1.5} />
                  </div>

                  <div className="absolute bottom-12 right-[-2rem] transform -rotate-90 origin-bottom-left">
                    <p className="text-sm text-[#4B6A9C] font-bold tracking-widest font-mono">

                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom Bar positioned absolutely at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 min-h-[120px] md:h-32 flex flex-col md:flex-row items-center justify-center md:justify-end px-4 md:px-8 lg:px-16 gap-3 md:gap-6 py-4 md:py-0 z-30"
          style={{ backgroundColor: '#31415A' }}
        >
          {isLoggedIn ? (
            <Button
              size="lg"
              onClick={() => navigate('/member')}
              className="w-full md:w-auto font-bold text-xs md:text-sm px-6 md:px-8 py-3 md:py-4 rounded-3xl shadow-lg hover:brightness-110 transition-all leading-tight h-auto text-center border-none"
              style={{ backgroundColor: '#FFFFFF', color: '#2C3A4F' }}
            >
              VIEW MY PROFILE
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={() => setShowMembershipModal(true)}
              className="w-full md:w-auto font-bold text-xs md:text-sm px-6 md:px-8 py-3 md:py-4 rounded-3xl shadow-lg hover:brightness-110 transition-all leading-tight h-auto text-center border-none"
              style={{ backgroundColor: '#FFFFFF', color: '#2C3A4F' }}
            >
              JOIN THE <br className="hidden md:block" />MEMBERSHIP
            </Button>
          )}
          <Button
            size="lg"
            className="w-full md:w-auto font-bold text-xs md:text-sm px-6 md:px-8 py-3 md:py-4 rounded-3xl shadow-lg hover:brightness-110 transition-all leading-tight h-auto text-center border-none flex items-center justify-center gap-3"
            style={{ backgroundColor: '#FFFFFF', color: '#2C3A4F' }}
            onClick={handleAboutMembershipClick}
          >
            <span>ABOUT THE <br className="hidden md:block" />MEMBERSHIP</span>
            <ChevronDown className={`w-4 md:w-5 h-4 md:h-5 transition-transform duration-300 ${showMembershipDetails ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </section>

      {showMembershipDetails && (
        <section
          ref={membershipDetailsRef}
          className="relative py-16 md:py-24 bg-cover bg-center min-h-[600px] flex items-center"
          style={{ backgroundImage: 'url(/membership_bg_hands.png)' }}
        >
          {/* Light Overlay for readability if needed, though image suggests it might not need much if text is contrasting */}
          {/* <div className="absolute inset-0 bg-black/20"></div> */}

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full h-full flex flex-col justify-center">


            <div className="grid md:grid-cols-2 gap-12 text-white mt-12 md:mt-0">
              <div
                className="space-y-12"
                data-aos="fade-up"
              >

                {/* What You Get */}
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-5xl font-bold text-white">What You Get?</h2>
                  <ul className="space-y-2 text-lg md:text-xl font-medium list-disc pl-6 text-white">
                    <li>Member-only rewards for every purchase</li>
                    <li>Exclusive member-only promotion</li>
                  </ul>
                </div>

                {/* How Do I Get My Free Coffee */}
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">How Do I Get My Free<br />Coffee?</h2>
                  <ul className="space-y-2 text-lg md:text-xl font-medium list-disc pl-6 text-white">
                    <li>Register/sign up your account</li>
                    <li>Verify Your Account</li>
                    <li>Log in through website</li>
                    <li>Collect the stamp and get your<br />free coffee!</li>
                  </ul>
                </div>

                <div className="pt-8 w-full">
                  <Button
                    size="lg"
                    className="font-bold text-lg px-8 py-6 rounded-xl shadow-lg hover:brightness-110 transition-all border-none bg-[#31415A] text-white hover:bg-[#4B6A9C]"
                    onClick={() => setShowFAQModal(true)}
                  >
                    FAQ
                  </Button>
                </div>

              </div>

              {/* Right side is empty for the background image to show */}
              <div></div>
            </div>
          </div>
        </section>
      )}

      {/* Membership Modal */}
      <MembershipModal
        isOpen={showMembershipModal}
        onClose={() => setShowMembershipModal(false)}
        onLoginSuccess={() => {
          setIsLoggedIn(true);
          // Refresh user data immediately after login
          authService.me().then(res => setCurrentUser(res.data.user)).catch(() => { });
        }}
      />

      {/* FAQ Modal */}
      <FAQModal
        isOpen={showFAQModal}
        onClose={() => setShowFAQModal(false)}
      />

    </div>
  );
};


export default Home;
